import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import { IEventDetailsDTO, IEventsDAO, IUpdateEventDTO } from '../../../api/events.dao';
import { showNotification } from '../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../infrastructure/alerts';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../global/rich-text-editor/utils/transpiler';
import { calcByteCodeSize } from '../../../infrastructure/utils/common.utils';
import { LONG_RICH_TEXT_LENGTH } from '../../../constants/common.constants';

export default class PlanningNotesFormVM {
    @mobx.observable
    public eventInfo: IEventDetailsDTO | IUpdateEventDTO;
    @mobx.observable
    public isSaving: boolean;
    @mobx.observable
    public isLoading: boolean;

    constructor(
        private eventId: number,
        private accountId: number,
        private intl: IntlShape,
        private saveCompletionCallback: () => void,
        public trackEventNotesMixPanel: (dataSaved: boolean) => void,
        @IoC.Inject private readonly eventsDAO: IEventsDAO = IoC.Container.get(IEventsDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
    ) {
        this.fetchEventInfo();
    }

    public formSubmitAction = async () => {
        mobx.runInAction(() => {
            this.isSaving = true;
        });
        try {
            const dto = this.getUpdateEventDTO();
            await this.eventsDAO.updateEvent(this.accountId, dto);
            showNotification(
                this.intl.formatMessage({
                    id: 'oe.common.planningNotesIsUpdated',
                    description: 'Notification about updated planning notes',
                    defaultMessage: 'Planning Notes is updated',
                })
            );
            this.trackEventNotesMixPanel(true);
            this.saveCompletionCallback();
            return true;
        } catch (error) {
            this.alertsService.alert({
                message: this.errorParsingService.extractMessage(error)
            });
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    public fetchEventInfo = async () => {
        mobx.runInAction(() => {
            this.isLoading = true;
        });

        try {
            const response = await this.eventsDAO.getEventDetails({
                eventId: this.eventId,
                accountId: this.accountId,
            });

            mobx.runInAction(() => {
                // Since CKEditor works with classes, but data is stored with inline styles,
                // all CKEditor values need to be converted to the appropriate format
                this.eventInfo = {
                    ...response,
                    planningNotes: transpileInlineStylesToClasses(response.planningNotes)
                };
            });
        } catch (error) {
            this.alertsService.alert({
                message: this.errorParsingService.extractMessage(error)
            });
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    public getPlanningNotesError(): boolean {
        return !!this.getPlanningNotesLengthError();
    }

    @mobx.action
    public getPlanningNotesLengthError(): string | null {
        return calcByteCodeSize(this.eventInfo.planningNotes) > LONG_RICH_TEXT_LENGTH
            ? `${this.intl.formatMessage({
                id: 'oe.common.planningNotes',
                defaultMessage: 'Planning Notes',
                description: 'Label text',
            })} is too long`
            : null;
    }

    private getUpdateEventDTO = (): IUpdateEventDTO => {
        // Since CKEditor works with classes, but data is stored with inline styles,
        // all CKEditor values need to be converted to the appropriate format
        return {
            ...this.eventInfo,
            planningNotes: transpileClassesToInlineStyles(this.eventInfo?.planningNotes || '')
        } as IUpdateEventDTO;
    };
};
